<template>
  <div
    class="bg-surface"
    :class="{ 'border-b': !isLast }"
  >
    <!-- Основная плашка подзадачи (клик по строке открывает подзадачу) -->
    <div
      class="d-flex align-center justify-space-between px-4 py-2 cursor-pointer select-none hover-bg-subtle"
      @click="handleRowClick"
    >
      <div class="d-flex align-center overflow-hidden min-w-0 flex-grow-1 flex-wrap ga-3">
        <!-- Номер подзадачи в сером кружочке -->
        <VAvatar
          size="24"
          color="surface-variant"
          variant="tonal"
          class="text-caption font-weight-medium flex-shrink-0 text-medium-emphasis"
        >
          {{ index + 1 }}
        </VAvatar>

        <!-- Исполнитель: Аватар + ФИО (Пирогова М.Ю. / Караваев С.Ю.) -->
        <div class="d-flex align-center ga-2 flex-shrink-0 subtask-performer-cell">
          <VAvatar size="24" class="border flex-shrink-0">
            <VImg v-if="performerAvatar" :src="performerAvatar" />
            <span v-else class="text-caption font-weight-medium">{{ performerInitials }}</span>
          </VAvatar>
          <VTooltip v-if="performerName" location="top" :text="performerFullName">
            <template #activator="{ props: tipProps }">
              <span
                v-bind="tipProps"
                class="text-body-1 font-weight-medium text-high-emphasis text-truncate min-w-0"
              >
                {{ performerName }}
              </span>
            </template>
          </VTooltip>
          <span v-else class="text-body-1 font-weight-medium text-medium-emphasis">
            —
          </span>
        </div>

        <!-- Разделитель | -->
        <div class="border-s flex-shrink-0" style="height: 16px;" />

        <!-- Срок (📅 20.09.25, 18:00) -->
        <div class="d-flex align-center ga-1 flex-shrink-0 subtask-deadline-cell">
          <VIcon icon="ri-calendar-line" size="16" class="text-medium-emphasis flex-shrink-0" />
          <span class="text-body-1 font-weight-medium text-high-emphasis">
            {{ deadlineFormatted || 'Без срока' }}
          </span>
        </div>

        <!-- Разделитель | -->
        <div class="border-s flex-shrink-0" style="height: 16px; margin-left: -4px; margin-right: -4px;" />

        <!-- Статус: НЕ чип! Точка ● + Текст (Ожидает / Завершено) + при необходимости огонек 🔥 -->
        <div class="d-flex align-center ga-2 flex-shrink-0">
          <span
            class="rounded-circle d-inline-block flex-shrink-0"
            :class="statusBgClass"
            style="width: 8px; height: 8px;"
          />
          <span class="text-body-1 font-weight-medium text-high-emphasis">
            {{ statusText }}
          </span>
          <!-- Огонек если срочная (по скриншоту) -->
          <VTooltip v-if="isHighPriority" location="top" text="Срочная задача">
            <template #activator="{ props: tipProps }">
              <VIcon
                v-bind="tipProps"
                icon="ri-fire-line"
                size="18"
                color="warning"
                class="ms-1 flex-shrink-0"
              />
            </template>
          </VTooltip>
        </div>

        <!-- Просрочка (только если просрочено и не завершено): | ⚠ Просрочено: 1 д 5 ч -->
        <template v-if="isOverdue && !isCompleted">
          <!-- Разделитель | -->
          <div class="border-s flex-shrink-0" style="height: 16px;" />
          <div class="d-flex align-center ga-1 text-error flex-shrink-0">
            <VIcon icon="ri-alert-line" size="16" color="error" class="flex-shrink-0" />
            <span class="text-body-1 font-weight-medium text-error">
              {{ overdueLabel }}
            </span>
          </div>
        </template>
      </div>

      <!-- Правая часть: шеврон (ТОЛЬКО для завершенных подзадач при наличии отчета или файлов) -->
      <div v-if="canExpand" class="flex-shrink-0 ms-2" @click.stop="toggleExpand">
        <VBtn
          variant="text"
          icon
          density="compact"
          size="small"
          color="secondary"
        >
          <VIcon :icon="isExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'" size="20" />
        </VBtn>
      </div>
    </div>

    <!-- Раскрывающаяся часть: Задача, Отчет, Вложения (скриншот 18 и 19) -->
    <VExpandTransition>
      <div v-if="isExpanded" class="border-t px-4 pb-3 pt-2">
        <!-- Задача: текст задачи -->
        <div class="d-flex align-baseline ga-2 mb-2">
          <span class="text-body-1 font-weight-medium text-primary flex-shrink-0">Задача:</span>
          <span class="text-body-1 text-medium-emphasis text-pre-wrap">{{ taskInstructionText }}</span>
        </div>

        <!-- Отчет: текст отчета (только у завершённой подзадачи) -->
        <div v-if="isCompleted" class="d-flex align-baseline ga-2 mb-2">
          <span class="text-body-1 font-weight-medium text-primary flex-shrink-0">Отчет:</span>
          <span class="text-body-1 text-medium-emphasis text-pre-wrap">{{ reportText || 'Отчет не предоставлен' }}</span>
        </div>

        <!-- Вложения к отчету (скриншот 19) -->
        <div v-if="isCompleted && reportFiles.length > 0" class="mt-2">
          <TaskReportFilesPopover :files="reportFiles" />
        </div>
      </div>
    </VExpandTransition>
  </div>
</template>

<script setup lang="ts">
import { useApiStore } from '@/modules/apiStore/stores/apiStore'
import type { IExistingFile } from '@/modules/files/types/IExistingFile'
import TaskReportFilesPopover from '@/modules/tasks/components/TaskReportFilesPopover.vue'
import type { IResponsibilityListItem } from '@/modules/tasks/types/IResponsibilityListItem'
import { formatShortName, getPersonDisplayName } from '@/utils/formatters'
import { getRandomAvatar } from '@/utils/getRandomAvatar'
import { computed, ref } from 'vue'

interface Props {
  subtask: IResponsibilityListItem
  index: number
  isLast?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isLast: false,
})

const emit = defineEmits<{
  (e: 'open', subtask: IResponsibilityListItem): void
}>()

const apiStore = useApiStore()
const isExpanded = ref(false)

const fallbackAvatars = new Map<string | number, string>()

const performerAvatar = computed(() => {
  const emp = props.subtask.responsible_employee
  if (emp?.avatar) return emp.avatar
  const id = emp?.id || props.subtask.id || props.index
  if (!fallbackAvatars.has(id)) {
    fallbackAvatars.set(id, getRandomAvatar())
  }
  return fallbackAvatars.get(id)!
})

const performerInitials = computed(() => {
  const emp = props.subtask.responsible_employee
  if (!emp) return '?'
  const last = emp.last_name?.[0] || ''
  const first = emp.first_name?.[0] || ''
  return `${last}${first}`.toUpperCase()
})

const performerName = computed(() => {
  return formatShortName(props.subtask.responsible_employee)
})

const performerFullName = computed(() => {
  return getPersonDisplayName(props.subtask.responsible_employee)
})

const deadlineFormatted = computed(() => {
  if (!props.subtask.deadline_time) return ''
  const d = new Date(props.subtask.deadline_time)
  if (isNaN(d.getTime())) return ''
  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const year = String(d.getFullYear()).slice(-2)
  const hours = String(d.getHours()).padStart(2, '0')
  const minutes = String(d.getMinutes()).padStart(2, '0')
  return `${day}.${month}.${year}, ${hours}:${minutes}`
})

const isCompleted = computed(() => {
  const s = props.subtask.status as string | undefined
  return s === 'completed' || s === 'passed'
})

const statusText = computed(() => {
  const status = props.subtask.status as string | undefined
  if (!status) return 'Ожидает'
  const statuses = apiStore.responsibilityEntriesStatuses
  return (statuses as Record<string, string>)[status] || props.subtask.status_label || (status === 'completed' || status === 'passed' ? 'Завершено' : status === 'in_progress' ? 'В работе' : 'Ожидает')
})

const statusBgClass = computed(() => {
  const s = props.subtask.status as string | undefined
  if (s === 'completed' || s === 'passed') return 'bg-success'
  if (s === 'in_progress') return 'bg-warning'
  if (s === 'overdue' || s === 'rejected') return 'bg-error'
  if (s === 'canceled' || s === 'cancelled') return 'bg-secondary'
  return 'bg-info'
})

const isHighPriority = computed(() => {
  return Boolean(props.subtask.is_high_priority)
})

const isOverdue = computed(() => {
  return Boolean(props.subtask.is_overdue)
})

const overdueLabel = computed(() => {
  if (!props.subtask.deadline_time) return 'Просрочено'
  const diff = Date.now() - new Date(props.subtask.deadline_time).getTime()
  if (diff <= 0) return 'Просрочено'
  const hours = Math.floor(diff / (1000 * 60 * 60))
  const days = Math.floor(hours / 24)
  const remHours = hours % 24
  if (days > 0) {
    return `Просрочено: ${days} д ${remHours} ч`
  }
  return `Просрочено: ${hours} ч`
})

const taskInstructionText = computed(() => {
  return props.subtask.task || props.subtask.instructions || props.subtask.title || ''
})

const reportText = computed(() => {
  if (props.subtask.last_report) return props.subtask.last_report
  if (props.subtask.report) return props.subtask.report
  if (Array.isArray(props.subtask.reports) && props.subtask.reports.length > 0) {
    return props.subtask.reports[props.subtask.reports.length - 1]?.report || ''
  }
  return ''
})

const reportFiles = computed<IExistingFile[]>(() => {
  return (props.subtask.files || []) as IExistingFile[]
})

// Раскрывается любая подзадача: текст задачи виден всегда, отчёт и вложения — после завершения
const canExpand = computed(() => {
  return Boolean(taskInstructionText.value || (isCompleted.value && (reportText.value || reportFiles.value.length > 0)))
})

function toggleExpand() {
  if (canExpand.value) {
    isExpanded.value = !isExpanded.value
  }
}

function handleRowClick() {
  emit('open', props.subtask)
}
</script>

<style scoped>
.hover-bg-subtle:hover {
  background-color: rgba(var(--v-theme-on-surface), 0.04);
}

.text-pre-wrap {
  white-space: pre-wrap;
  word-break: break-word;
}

.subtask-performer-cell {
  width: 180px;
  min-width: 180px;
  max-width: 180px;
  flex: 0 0 180px;
  overflow: hidden;
}

.subtask-deadline-cell {
  width: 140px;
  min-width: 140px;
  max-width: 140px;
  flex: 0 0 140px;
}
</style>
